<template>
  <div class="create-page">

    <div class="create-grid">

      <!-- LEFT: Form -->
      <div class="form-panel">
        <div class="form-inner">
          <div class="form-header">
            <i class="bx bx-gift form-icon"></i>
            <h1>Create a Wishboard</h1>
            <p>Set it up in under a minute. Share a link. Watch messages arrive.</p>
          </div>

          <form @submit.prevent="submitForm" class="wb-form">

            <div class="field-group">
              <label class="field-label">Board title</label>
              <input
                type="text"
                class="field-input"
                v-model="wishboard_name"
                placeholder="e.g. Happy Birthday"
                autocomplete="off"
              />
            </div>

            <div class="field-group">
              <label class="field-label">Who is it for?</label>
              <input
                type="text"
                class="field-input"
                v-model="wishboard_recipient"
                placeholder="e.g. Sarah"
                autocomplete="off"
              />
            </div>

            <div class="field-group">
              <label class="field-label">Plan</label>
              <div class="plan-options">
                <label class="plan-option" :class="{ active: wishboard_selection === 'free' }">
                  <input type="radio" v-model="wishboard_selection" value="free" />
                  <div class="plan-body">
                    <div class="plan-name">{{ plans.freeName }}</div>
                    <div class="plan-detail">{{ plans.freeDetail }}</div>
                    <div class="plan-note">{{ plans.freeNote }}</div>
                  </div>
                </label>
                <label class="plan-option plan-option-priced" :class="{ active: wishboard_selection === 'paid' }">
                  <input type="radio" v-model="wishboard_selection" value="paid" />
                  <div class="plan-body">
                    <div class="plan-name">{{ plans.premiumName }}</div>
                    <!-- The price is the reason to pick this plan, so it sits
                         directly under the name at a comparable weight to it.
                         It used to sit at the end of the note line as
                         "For the whole crowd . $2.99", where it read as a footnote. -->
                    <div class="plan-price">{{ priceHint }}</div>
                    <div class="plan-detail">{{ plans.premiumDetail }}</div>
                    <div class="plan-note">{{ plans.premiumNote }}</div>
                  </div>
                </label>
              </div>
            </div>

            <div class="errors" v-if="errors.length">
              <p v-for="error in errors" :key="error">{{ error }}</p>
            </div>

            <button type="submit" class="btn-submit">
              Create Wishboard <i class="bx bx-right-arrow-alt"></i>
            </button>

          </form>
        </div>
      </div>

      <!-- RIGHT: Static mock board -->
      <div class="demo-panel">
        <div class="demo-panel-inner">
          <div class="demo-header">
            <span class="demo-label">What your contributors will see</span>
          </div>

          <div class="mock-board">
            <div class="mock-board-top">
              <div class="mock-board-title">Happy Birthday, Sarah</div>
              <div class="mock-board-meta">24 messages · created by James</div>
              <div class="mock-actions">
                <span class="mock-btn"><i class="bx bx-plus"></i> Add a wish</span>
                <span class="mock-btn-ghost"><i class="bx bx-share-alt"></i></span>
              </div>
            </div>
            <div class="mock-cards">
              <div class="mock-card" v-for="msg in mockMessages" :key="msg.name" :class="msg.size">
                <div class="mock-from">{{ msg.name }}</div>
                <div class="mock-msg">{{ msg.message }}</div>
                <img v-if="msg.gif" :src="msg.gif" class="mock-gif" alt="" />
              </div>
            </div>
          </div>

          <p class="demo-caption">
            Anyone with the link can leave a message — no account needed.
          </p>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import axios from '../axiosConfig'
import { planCopy, pricingForBuyer } from '../utils/pricing'
import { toast } from 'bulma-toast'

export default {
  name: 'WishboardCreate',
  data() {
    return {
      wishboard_name: '',
      wishboard_creator: '',
      wishboard_recipient: '',
      wishboard_selection: 'free',
      plans: planCopy(),
      buyer: null,
      tierLabels: null,
      errors: [],
      mockMessages: [
        { name: 'Priya M.', message: 'Happy birthday! Hope this year brings everything you deserve.', gif: 'https://media.giphy.com/media/26FPIV12CYbDSVIR2/giphy.gif', size: 'card-med' },
        { name: 'James T.', message: 'You\'ve been the backbone of this team. Wishing you the very best.', gif: null, size: 'card-tall' },
        { name: 'Carlos R.', message: 'So lucky to work with someone as kind as you!', gif: 'https://media.giphy.com/media/l4pTeMhvDmNasKWUU/giphy.gif', size: 'card-tall' },
        { name: 'Lisa K.', message: 'Here\'s to you — cheers! 🥂', gif: null, size: 'card-sm' },
        { name: 'Anika S.', message: 'Wishing you a wonderful day filled with joy.', gif: 'https://media.giphy.com/media/3o7qDE31B2gsTCn98A/giphy.gif', size: 'card-med' },
        { name: 'Tom B.', message: 'Many happy returns! You\'re one in a million.', gif: null, size: 'card-sm' },
      ],
    }
  },
  mounted() {

  // Decide the price tier from the visitor's own locale, and pick up the
  // display labels the backend publishes so the figure shown here matches
  // what they will actually be charged. Falls back to the labels in
  // utils/pricing.js if the request fails.
  this.buyer = pricingForBuyer(this.tierLabels);
  axios
    .get('/mywishboard/api/config/')
    .then((response) => {
      const tiers = response.data && response.data.price_tiers;
      if (tiers) {
        this.tierLabels = tiers;
        this.buyer = pricingForBuyer(tiers);
      }
    })
    .catch(() => {
      /* keep the fallback labels from utils/pricing.js */
    });
    this.emitter.emit('showme', 'false')
    this.validate_tok()
  },
  computed: {
    // The price this visitor will pay, so the plan card never advertises a figure
    // that does not match checkout. Western visitors see the USD price only;
    // everyone else sees it too, because an English-locale visitor in Australia
    // or Singapore is far more likely to pay in dollars than rupees.
    priceHint() {
      if (!this.buyer) return '';
      if (this.buyer.isWestern) {
        return this.buyer.priceLabel;
      }
      return this.buyer.priceLabel + ' or ' + this.buyer.intlLabel;
    },
  },
  methods: {
    // Guards a route that requires a signed-in user.
    //
    // This used to fire the verify request immediately on mount, which raced the
    // token: a user arriving here straight after Google sign-in had their
    // credential stored in Login.onSignIn, but this page mounted and posted
    // before the store getter had it, so the request went out with
    // `Authorization: Bearer ` (empty). The backend answers
    // {"error": "Token not available"}, which this treated as "not signed in"
    // and redirected to /Login -- which re-initialised Google Sign-In, producing
    // the "initialize() is called multiple times" and "Missing required
    // parameter: client_id" warnings, then bounced back here. A redirect loop.
    //
    // It showed up worst for genuinely new Google accounts, because they land
    // here with no existing boards; users going to /Wishboardlist mostly missed
    // it. Two fixes: don't send the request with no token at all, and give the
    // store a tick to settle before deciding the user is signed out.
    validate_tok() {
      const token = this.$store.getters.getGToken
      if (!token) {
        // No credential yet. This is either a race with sign-in completing, or a
        // genuine guest. Wait one tick and re-read before bouncing to Login.
        this.$nextTick(() => {
          if (!this.$store.getters.getGToken) {
            this.$router.push({ name: 'Login', query: { redirect: this.$route.fullPath } })
          }
        })
        return
      }

      axios.post('/mywishboard/api/wishboard/user/token/verify/', {}, {
        headers: { 'Authorization': 'Bearer ' + token }
      })
      .then(response => {
        if (!(response.data && response.data.success === 'Token is available')) {
          this.$store.commit('setGToken', '')
          this.$router.push({ name: 'Login', query: { redirect: this.$route.fullPath } })
        }
      })
      .catch(() => {
        this.$store.commit('setGToken', '')
        this.$router.push({ name: 'Login', query: { redirect: this.$route.fullPath } })
      })
    },

    submitForm() {
      this.errors = []
      if (!this.wishboard_name.trim()) this.errors.push('Please provide a title for the Wishboard')
      if (!this.wishboard_recipient.trim()) this.errors.push('Please provide a recipient for the Wishboard')
      if (this.errors.length) return

      axios.post('/mywishboard/api/wishboard/create/', {
        wishboard_creator: this.$store.getters.getUser,
        wishboard_recipient: this.wishboard_recipient,
        wishboard_title: this.wishboard_name,
        wishboard_created_date: new Date().toJSON()
      }, {
        headers: { 'Authorization': 'Bearer ' + this.$store.getters.getGToken }
      })
      .then(response => {
        localStorage.setItem('wishboard_id', response.data.wishboard_id)
        this.$store.commit('setWishboardId', response.data.wishboard_id)
        if (this.wishboard_selection === 'paid') {
          this.$router.push({ name: 'Payment', query: { wishboard_id: response.data.wishboard_id } })
        } else {
          this.$router.push({ path: 'Wishboard', query: { wishboard_id: response.data.wishboard_id } })
        }
      })
      .catch(error => {
        if (error.response) {
          for (const prop in error.response.data) {
            this.errors.push(`${prop}: ${error.response.data[prop]}`)
          }
        } else {
          this.errors.push('Something went wrong. Please try again.')
        }
      })
    }
  }
}
</script>

<style scoped>
*, *::before, *::after { box-sizing: border-box; }

.create-page {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  min-height: 100vh;
  background: #fff;
  display: flex;
  align-items: stretch;
}

/* ── GRID ── */
.create-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  width: 100%;
  min-height: 100vh;
}

/* ── FORM PANEL ── */
.form-panel {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 48px;
  border-right: 1px solid #f0f0f0;
}

.form-inner {
  width: 100%;
  max-width: 420px;
}

.form-header {
  text-align: center;
  margin-bottom: 40px;
}

.form-icon {
  font-size: 2rem;
  color: #e8a020;
  display: block;
  margin-bottom: 14px;
}

.form-header h1 {
  font-size: 1.65rem;
  font-weight: 800;
  letter-spacing: -0.4px;
  color: #0a0a0a;
  margin-bottom: 10px;
}

.form-header p {
  font-size: 0.88rem;
  color: #888;
  line-height: 1.6;
}

/* ── FIELDS ── */
.wb-form { display: flex; flex-direction: column; gap: 24px; }

.field-group { display: flex; flex-direction: column; gap: 7px; }

.field-label {
  font-size: 0.78rem;
  font-weight: 700;
  color: #444;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.field-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 0.95rem;
  color: #111;
  outline: none;
  transition: border-color 0.18s, box-shadow 0.18s;
  background: #fafafa;
}

.field-input:focus {
  border-color: #e8a020;
  box-shadow: 0 0 0 3px rgba(232,160,32,0.12);
  background: #fff;
}

/* ── PLAN OPTIONS ── */
.plan-options {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.plan-option {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 16px;
  border: 1.5px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.18s, background 0.18s;
  background: #fafafa;
}

.plan-option input[type="radio"] { margin-top: 3px; accent-color: #e8a020; }

.plan-option.active {
  border-color: #e8a020;
  background: #fdf3e0;
}

.plan-name {
  font-size: 0.88rem;
  font-weight: 700;
  color: #111;
  margin-bottom: 3px;
}

.plan-detail {
  font-size: 0.75rem;
  color: #888;
}
.plan-note {
  margin-top: 3px;
  font-size: 0.78rem;
  line-height: 1.35;
  color: #7a7a8c;
  font-weight: 400;
}
/* The price, on its own line under the plan name.

   Sized between the plan name (0.88rem) and the detail line (0.82rem) so the
   eye lands on it first without it shouting over the plan name. Amber is the
   same accent the rest of the page uses for the active plan, so it reads as
   belonging rather than as an alert. tabular-nums stops the price jittering
   as the label switches between "$2.99" and "$2.99 or ₹99". */
.plan-price {
  font-size: 1.02rem;
  font-weight: 700;
  color: #e8a020;
  letter-spacing: -0.2px;
  font-variant-numeric: tabular-nums;
  margin-bottom: 2px;
}
/* With a price line present, the detail line needs a touch more air above it
   so "Premium / $2.99 / Up to 200 messages" reads as three ranks. */
.plan-option-priced .plan-detail { margin-top: 7px; }

/* ── ERRORS ── */
.errors {
  background: #fff5f5;
  border: 1px solid #fcc;
  border-radius: 8px;
  padding: 14px 16px;
}

.errors p {
  font-size: 0.82rem;
  color: #c00;
  margin: 0 0 4px;
}

.errors p:last-child { margin-bottom: 0; }

/* ── SUBMIT ── */
.btn-submit {
  width: 100%;
  padding: 15px;
  background: #0a0a0a;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background 0.18s, transform 0.15s;
}

.btn-submit:hover {
  background: #2a2a2a;
  transform: translateY(-1px);
}

/* ── DEMO PANEL ── */
.demo-panel {
  background: #fafafa;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 40px;
  overflow-y: auto;
}

.demo-panel-inner {
  width: 100%;
  max-width: 520px;
}

.demo-header {
  margin-bottom: 14px;
}

.demo-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: #aaa;
}

/* ── MOCK BOARD ── */
.mock-board {
  background: #fff;
  border: 1px solid #e4e4e4;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0,0,0,0.04), 0 12px 40px rgba(0,0,0,0.08);
}

.mock-board-top {
  padding: 20px 22px 16px;
  border-bottom: 1px solid #f2f2f2;
  text-align: center;
}

.mock-board-title {
  font-size: 1.05rem;
  font-weight: 800;
  color: #0a0a0a;
  margin-bottom: 4px;
  letter-spacing: -0.2px;
}

.mock-board-meta {
  font-size: 0.72rem;
  color: #bbb;
  margin-bottom: 14px;
  letter-spacing: 0.2px;
}

.mock-actions {
  display: flex;
  justify-content: center;
  gap: 8px;
}

.mock-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #0a0a0a;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 7px 14px;
  border-radius: 6px;
  cursor: default;
  letter-spacing: 0.1px;
}

.mock-btn-ghost {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  color: #888;
  font-size: 1rem;
  cursor: default;
}

/* masonry-style card grid */
.mock-cards {
  padding: 16px;
  columns: 2;
  column-gap: 10px;
}

.mock-card {
  break-inside: avoid;
  background: #fafafa;
  border: 1px solid #efefef;
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 10px;
}

.mock-from {
  font-size: 0.65rem;
  font-weight: 700;
  color: #ccc;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 6px;
}

.mock-msg {
  font-size: 0.78rem;
  color: #444;
  line-height: 1.55;
}

.mock-gif {
  width: 100%;
  border-radius: 6px;
  margin-top: 10px;
  display: block;
}

.demo-caption {
  font-size: 0.76rem;
  color: #bbb;
  margin-top: 12px;
  line-height: 1.6;
  text-align: center;
}

/* ── RESPONSIVE ── */
@media (max-width: 900px) {
  .create-grid {
    grid-template-columns: 1fr;
    min-height: auto;
  }

  .form-panel {
    padding: 48px 24px;
    border-right: none;
    border-bottom: 1px solid #f0f0f0;
  }

  .demo-panel {
    padding: 40px 24px;
  }

  .demo-frame-wrap { height: 380px; }
}

@media (max-width: 480px) {
  .form-panel { padding: 36px 20px; }
  .plan-options { grid-template-columns: 1fr; }
  .demo-frame-wrap { height: 300px; }
}
</style>
